import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getCollegeInfo, generateReceiptFooter, fetchLogoAsBase64, fetchReceiptTerms } from './receiptHeaderUtils';
import { ensureRupeeFont, RUPEE_FONT, formatINR } from './jspdfFonts';
import { formatDate } from '../utils/dateTimeUtils';

// Common header generator with logo support
const generateHeader = async (doc, collegeInfo, title = 'ADMISSION RECEIPT', logoBase64 = null) => {
  const resolvedFont = await ensureRupeeFont(doc);
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  let yPos = 15;

  const collegeName = collegeInfo?.college_name || collegeInfo?.name || 'Institution';
  const collegeAddress = collegeInfo?.address || '';
  const collegeCity = collegeInfo?.city || '';
  const collegeState = collegeInfo?.state || '';
  const collegePincode = collegeInfo?.pincode || collegeInfo?.pin_code || '';
  const collegePhone = collegeInfo?.phone || '';
  const collegeEmail = collegeInfo?.email || '';
  const collegeLogo = logoBase64 || collegeInfo?.logo_base64 || collegeInfo?.logo_url || collegeInfo?.logo || '';

  // Add logo if available
  let logoAdded = false;
  if (collegeLogo) {
    try {
      const logoWidth = 20;
      const logoHeight = 20;
      const logoX = margin;
      
      if (collegeLogo.startsWith('data:image')) {
        doc.addImage(collegeLogo, 'PNG', logoX, yPos - 5, logoWidth, logoHeight);
        logoAdded = true;
      }
    } catch (e) {
      console.log('Could not add logo:', e);
    }
  }

  // Adjust text position based on logo
  const textStartX = logoAdded ? margin + 25 : pageWidth / 2;
  // Calculate max width for text (considering logo space)
  const maxTextWidth = logoAdded ? (pageWidth - margin - textStartX - 5) : (pageWidth - 2 * margin);

  // College Name
  doc.setFontSize(16);
  doc.setFont(resolvedFont, 'bold');
  doc.setTextColor(0, 51, 102);
  
  if (logoAdded) {
    doc.text(collegeName.toUpperCase(), textStartX, yPos + 2);
  } else {
    doc.text(collegeName.toUpperCase(), pageWidth / 2, yPos, { align: 'center' });
  }
  yPos += 6;

  // Full Address with text wrapping for long addresses
  doc.setFontSize(9);
  doc.setFont(resolvedFont, 'normal');
  doc.setTextColor(60, 60, 60);

  if (collegeAddress) {
    // Split long address into multiple lines
    const wrappedAddress = doc.splitTextToSize(collegeAddress, maxTextWidth);
    wrappedAddress.forEach((line) => {
      if (logoAdded) {
        doc.text(line, textStartX, yPos);
      } else {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      }
      yPos += 4;
    });
  }

  const cityStatePincode = [collegeCity, collegeState, collegePincode].filter(Boolean).join(', ');
  if (cityStatePincode) {
    const wrappedCity = doc.splitTextToSize(cityStatePincode, maxTextWidth);
    wrappedCity.forEach((line) => {
      if (logoAdded) {
        doc.text(line, textStartX, yPos);
      } else {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      }
      yPos += 4;
    });
  }

  // Phone and Email with wrapping
  const contactParts = [];
  if (collegePhone) contactParts.push(`Phone: ${collegePhone}`);
  if (collegeEmail) contactParts.push(`Email: ${collegeEmail}`);
  
  if (contactParts.length > 0) {
    doc.setFontSize(8);
    const contactText = contactParts.join('   |   ');
    const wrappedContact = doc.splitTextToSize(contactText, maxTextWidth);
    wrappedContact.forEach((line) => {
      if (logoAdded) {
        doc.text(line, textStartX, yPos);
      } else {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      }
      yPos += 4;
    });
    yPos += 1;
  }

  // Ensure yPos is below logo
  if (logoAdded) {
    yPos = Math.max(yPos, 35);
  }

  // Decorative double line
  doc.setDrawColor(0, 51, 102);
  doc.setLineWidth(1);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 2;
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Receipt Title
  doc.setFontSize(14);
  doc.setFont(resolvedFont, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(title, pageWidth / 2, yPos, { align: 'center' });
  yPos += 3;

  const titleWidth = doc.getTextWidth(title);
  doc.setLineWidth(0.5);
  doc.line((pageWidth - titleWidth) / 2 - 5, yPos, (pageWidth + titleWidth) / 2 + 5, yPos);
  yPos += 8;

  doc.setTextColor(0, 0, 0);
  return yPos;
};

export const generateAdmissionReceipt = async (admissionData, collegeInfo = null) => {
  console.log('=== ADMISSION RECEIPT GENERATOR CALLED ===');
  
  // Get college info
  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
  }
  
  // Fetch logo as base64 if URL is provided
  let logoBase64 = collegeInfo.logo_base64 || null;
  if (!logoBase64 && (collegeInfo.logo_url || collegeInfo.logo)) {
    logoBase64 = await fetchLogoAsBase64(collegeInfo.logo_url || collegeInfo.logo);
  }
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  // Register Unicode-capable font so the ₹ symbol renders correctly.
  // Returns 'FreeSans' on success, 'helvetica' on failure (₹ won't render).
  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';
  
  // Generate Header with College Info and Logo
  let yPos = await generateHeader(doc, collegeInfo, 'ADMISSION RECEIPT', logoBase64);
  
  // Receipt Info
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  const receiptNo = admissionData?.receipt_no || admissionData?.admission_no || 'N/A';
  doc.text(`Receipt No: ${receiptNo}`, 20, yPos);
  doc.text(`Date: ${formatDate(admissionData?.admission_date || new Date())}`, pageWidth - 60, yPos);
  yPos += 10;
  
  // Student Info Section
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.setFillColor(240, 240, 240);
  doc.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
  doc.text('STUDENT INFORMATION', 22, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  
  const studentName = admissionData?.student_name ?? 'N/A';
  const admissionNo = admissionData?.admission_no ?? 'N/A';
  const courseName = admissionData?.course_name ?? 'N/A';
  const session = admissionData?.session ?? 'N/A';
  const fatherName = admissionData?.father_name ?? '';
  const phone = admissionData?.phone ?? '';
  
  doc.text(`Student Name: ${studentName}`, 20, yPos);
  doc.text(`Admission No: ${admissionNo}`, pageWidth / 2, yPos);
  yPos += 6;
  
  doc.text(`Course: ${courseName}`, 20, yPos);
  doc.text(`Session: ${session}`, pageWidth / 2, yPos);
  yPos += 6;
  
  if (fatherName) {
    doc.text(`Father's Name: ${fatherName}`, 20, yPos);
  }
  if (phone) {
    doc.text(`Phone: ${phone}`, pageWidth / 2, yPos);
  }
  yPos += 12;
  
  // Fee Details Section
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.setFillColor(240, 240, 240);
  doc.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
  doc.text('FEE DETAILS', 22, yPos);
  yPos += 8;
  
  const feeData = [];
  // Resolve all amounts from caller-provided keys (StudentAdmission +
  // AdmissionReport pass: total_course_fee, discount_amount, scholarship_amount,
  // booking_amount, admission_fee, balance_fee).
  const totalFee = parseFloat(
    admissionData?.total_course_fee ?? admissionData?.total_fee ?? 0
  ) || 0;
  const discount = parseFloat(admissionData?.discount_amount ?? 0) || 0;
  const scholarship = parseFloat(admissionData?.scholarship_amount ?? 0) || 0;
  const netFee = parseFloat(
    admissionData?.net_fee ?? (totalFee - discount - scholarship)
  ) || 0;
  const bookingAmt = parseFloat(admissionData?.booking_amount ?? 0) || 0;
  const admissionFee = parseFloat(admissionData?.admission_fee ?? 0) || 0;
  const amountPaid = parseFloat(
    admissionData?.amount_paid ?? (bookingAmt + admissionFee)
  ) || 0;
  const balanceDue = parseFloat(
    admissionData?.balance_fee ?? admissionData?.balance_due ?? (netFee - amountPaid)
  ) || 0;
  
  feeData.push(['Total Course Fee', `${CURRENCY} ${formatINR(totalFee)}`]);
  if (discount > 0) feeData.push(['Discount', `${CURRENCY} ${formatINR(discount)}`]);
  if (scholarship > 0) feeData.push(['Scholarship', `${CURRENCY} ${formatINR(scholarship)}`]);
  feeData.push(['Net Payable Fee', `${CURRENCY} ${formatINR(netFee)}`]);
  if (bookingAmt > 0) feeData.push(['Seat Booking Paid', `${CURRENCY} ${formatINR(bookingAmt)}`]);
  if (admissionFee > 0) feeData.push(['Admission Fee Paid', `${CURRENCY} ${formatINR(admissionFee)}`]);
  feeData.push(['Total Amount Paid', `${CURRENCY} ${formatINR(amountPaid)}`]);
  feeData.push(['Balance Due', `${CURRENCY} ${formatINR(balanceDue)}`]);
  
  autoTable(doc, {
    startY: yPos,
    body: feeData,
    theme: 'striped',
    styles: { fontSize: 10, font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 100, fontStyle: 'normal' },
      1: { cellWidth: 70, halign: 'right', fontStyle: 'bold' }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 10;
  
  // Payment Details
  if (admissionData?.payment_modes && admissionData.payment_modes.length > 0) {
    doc.setFontSize(11);
    doc.setFont(resolvedFont, 'bold');
    doc.text('Payment Breakdown', 20, yPos);
    yPos += 5;
    
    const paymentTable = admissionData.payment_modes.map(pm => [
      `${pm.mode || 'N/A'}${pm.transaction_number ? ` (Ref: ${pm.transaction_number})` : ''}`,
      `${CURRENCY} ${formatINR(pm.amount || 0)}`
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Payment Mode', 'Amount']],
      body: paymentTable,
      theme: 'plain',
      headStyles: { fillColor: [230, 230, 230], textColor: 0 },
      styles: { fontSize: 9, font: resolvedFont },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 70, halign: 'right' }
      }
    });
    
    yPos = doc.lastAutoTable.finalY + 8;
  }
  
  // Footer with tenant-specific terms
  const terms = await fetchReceiptTerms('admission');
  generateReceiptFooter(doc, collegeInfo, terms);
  
  // Save PDF
  const fileName = `AdmissionReceipt_${admissionNo}_${Date.now()}.pdf`;
  doc.save(fileName);
  return doc;
};

export default generateAdmissionReceipt;
